import { Disposable } from "@codesandbox/pitcher-client";
import * as vscode from "vscode";
import { registerExtension } from "vscode/extensions";
import type { IEditorService } from "vscode/services";

import type { EditorHistoryService } from "../editor-history";

import { CodeiumCompletionProvider } from "./CompletionProvider";
import { acceptCompletion } from "./codeium-core";

export class CodeiumService extends Disposable {
  private statusBarItem: vscode.StatusBarItem | undefined = undefined;
  private timer: NodeJS.Timeout | undefined = undefined;

  constructor(
    private username: string,
    editorService: IEditorService,
    historyService: EditorHistoryService,
    onAcceptSuggestion: () => void,
  ) {
    super();
    this.initialize(editorService, historyService, onAcceptSuggestion);
  }

  private async initialize(
    editorService: IEditorService,
    historyService: EditorHistoryService,
    onAcceptSuggestion: () => void,
  ) {
    this.addDisposable(
      vscode.commands.registerCommand(
        "codeium.acceptCompletion",
        (completionId: string, callback?: () => void) => {
          onAcceptSuggestion();

          acceptCompletion(this.username, completionId)
            .then(() => {
              callback?.();
            })
            .catch((err) => {
              // eslint-disable-next-line
              console.error(err);
            });
        },
      ),
    );

    await this.createStatusBarItem();
    const dispose = vscode.languages.registerInlineCompletionItemProvider(
      [
        "typescript",
        "typescriptreact",
        "javascriptreact",
        "css",
        "rust",
        "sql",
        "python",
        "vue",
        "svelte",
        "php",
        "lua",
        "elixir",
        "ruby",
        "go",
      ].map((l) => {
        return {
          language: l,
        };
      }),
      new CodeiumCompletionProvider(
        this.username,
        editorService,
        historyService,
        this.setStatusBarTitle,
      ),
    );

    this.addDisposable(dispose);
  }

  private async createStatusBarItem() {
    const { getApi, dispose } = registerExtension(
      {
        name: "Codeium Extension for CodeSandbox",
        publisher: "codesandbox",
        version: "1.0.0",
        engines: {
          vscode: "*",
        },
        contributes: {},
      },
      1,
    );

    this.addDisposable({ dispose });

    const vscode = await getApi();
    this.statusBarItem = vscode.window.createStatusBarItem(
      vscode.StatusBarAlignment.Right,
      Infinity, // this is really important
    );

    this.setStatusBarTitle(false);

    this.statusBarItem.command = "codeium.open";

    this.statusBarItem.show();

    this.addDisposable({
      dispose: () => {
        this.statusBarItem?.dispose();
      },
    });
  }

  private setStatusBarTitle = (loading: boolean) => {
    if (this.timer) {
      clearTimeout(this.timer);
    }

    this.timer = setTimeout(() => {
      if (!this.statusBarItem) return;

      this.statusBarItem.text = loading
        ? "Codeium: $(sync~spin)"
        : "Codeium: {…}";

      this.statusBarItem.show();
    }, 100);
  };
}
